// Purchase Order Receipt list filters — used by the tenant-wide
// Receipts page (listKey `purchase_order_receipts_main`).
//
// Per-PO embedded receipts (listKey `purchase_order_receipt`) don't
// use these — that view passes its own queryFn rather than going
// through the registered filter pipeline.

import {
	PurchaseOrderReceiptDispositionStatus,
	PurchaseOrderReceiptDispositionStatusLabels,
} from '@supply-mover-2-0/shared';
import {
	createDateRangeFilter,
	createEntityLookupFilter,
	createKeywordFilter,
	createStatusFilter,
} from './factories';
import type { FilterRegistry } from './types';

const RECEIPT_LIST_KEY = 'purchase_order_receipts_main';

export const purchaseOrderReceiptFilters: FilterRegistry = {
	receiptKeywords: {
		...createKeywordFilter({
			label: 'Search',
			placeholder:
				'Search by receipt number, packing slip, carrier, or notes...',
			objectTable: 'purchase_order_receipt',
			listKeys: [RECEIPT_LIST_KEY],
			fulltextFields: [
				'externalId',
				'packingSlipNumber',
				'proNumber',
				'carrierName',
				'notes',
			],
		}),
		queryParamName: 'receiptKeywords',
	},

	receiptDispositionStatus: {
		...createStatusFilter({
			label: 'Disposition',
			options: Object.values(PurchaseOrderReceiptDispositionStatus).map(
				(status) => ({
					id: status,
					label: PurchaseOrderReceiptDispositionStatusLabels[status],
				}),
			),
			objectTable: 'purchase_order_receipt',
			fieldName: 'dispositionStatus',
			listKeys: [RECEIPT_LIST_KEY],
		}),
		queryParamName: 'receiptDispositionStatus',
	},

	receiptVendor: {
		...createEntityLookupFilter({
			label: 'Vendor',
			placeholder: 'Search vendors...',
			apiEndpoint: '/vendors',
			objectTable: 'purchase_order_receipt',
			fieldName: 'fk_vendor__id',
			listKeys: [RECEIPT_LIST_KEY],
			displayFields: {
				id: 'id',
				primary: 'name',
			},
			colorScheme: 'blue',
			searchable: true,
		}),
		queryParamName: 'receiptVendor',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	receiptWarehouse: {
		...createEntityLookupFilter({
			label: 'Warehouse',
			placeholder: 'Search warehouses...',
			apiEndpoint: '/warehouses',
			objectTable: 'purchase_order_receipt',
			fieldName: 'fk_warehouse__id',
			listKeys: [RECEIPT_LIST_KEY],
			displayFields: {
				id: 'id',
				primary: 'name',
			},
			colorScheme: 'gray',
			searchable: true,
		}),
		queryParamName: 'receiptWarehouse',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	receiptDateRange: {
		...createDateRangeFilter({
			label: 'Receipt Date',
			objectTable: 'purchase_order_receipt',
			dateField: 'receiptDate',
			listKeys: [RECEIPT_LIST_KEY],
		}),
		queryParamName: 'receiptDateRange',
		colorScheme: 'green',
	},

	// receiptAssignedUser intentionally NOT registered here — that
	// filter joins through receipt → detail → exception, which only
	// becomes meaningful once Phase 6 stands up the exception entity
	// surface. The "My Open" tab lands then.
};
